import NumberFlow from "@number-flow/react";
import { money, pct } from "../lib/calc";
import { duelCall, type DuelView } from "../lib/duel";

type Props = {
  duel: DuelView;
  youName: string;
  themName: string;
  themHint?: string;
  rivalMode: "print" | "offer";
  offerText: string;
  onRivalMode: (mode: "print" | "offer") => void;
  onOfferText: (next: string) => void;
  onNudgeYou: (delta: number) => void;
  onNudgeThem: (delta: number) => void;
};

function Pad({
  label,
  onNudge,
}: {
  label: string;
  onNudge: (delta: number) => void;
}) {
  return (
    <div className="duel-pad" role="group" aria-label={label}>
      <button type="button" onClick={() => onNudge(-0.05)} aria-label={`${label}, down 0.05`}>
        −
      </button>
      <button type="button" onClick={() => onNudge(0.05)} aria-label={`${label}, up 0.05`}>
        +
      </button>
    </div>
  );
}

export function RateDuel({
  duel,
  youName,
  themName,
  themHint,
  rivalMode,
  offerText,
  onRivalMode,
  onOfferText,
  onNudgeYou,
  onNudgeThem,
}: Props) {
  const payback =
    duel.breakEvenMonths != null && duel.monthlyGap > 0 ? `${duel.breakEvenMonths} mo` : "—";

  return (
    <div className="duel">
      <div className="duel-lanes">
        <div className="duel-lane is-you">
          <span>You · stay</span>
          <strong>
            <NumberFlow
              value={duel.youRate}
              format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            />
            %
          </strong>
          <Pad label="Your rate" onNudge={onNudgeYou} />
          <small>{youName}</small>
          <em>{money(duel.youMonthly)} / mo</em>
        </div>
        <p className="duel-vs" aria-hidden>
          VS
        </p>
        <div className="duel-lane is-them">
          <span>{rivalMode === "offer" ? "Their offer" : "Cited print"}</span>
          <strong>
            <NumberFlow
              value={duel.themRate}
              format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            />
            %
          </strong>
          <Pad label="Their rate" onNudge={onNudgeThem} />
          <small>{themName}</small>
          <em>{money(duel.themMonthly)} / mo</em>
        </div>
      </div>

      <div
        className="duel-tug"
        role="img"
        aria-label={`You ${pct(duel.youRate)} versus ${pct(duel.themRate)}.`}
      >
        <i className="is-you" style={{ width: `${Math.round(duel.youShare * 100)}%` }} />
        <i className="is-them" />
      </div>

      <div className="cluster">
        <p>
          <span>Month</span>
          <b>{money(duel.monthlyGap)}</b>
        </p>
        <p>
          <span>Year</span>
          <b>{money(duel.yearlyGap)}</b>
        </p>
        <p>
          <span>Payback</span>
          <b>{payback}</b>
        </p>
        <p>
          <span>Gap</span>
          <b>{duel.close ? "Close" : pct(duel.youRate - duel.themRate)}</b>
        </p>
      </div>

      <p className="duel-call">{duelCall(duel)}</p>

      <div className="rival-switch" role="tablist" aria-label="What number sits on the harbour side">
        <button
          type="button"
          role="tab"
          className="is-print"
          aria-selected={rivalMode === "print"}
          onClick={() => onRivalMode("print")}
        >
          Cited print
          <small>Lender page number we already checked</small>
        </button>
        <button
          type="button"
          role="tab"
          className="is-offer"
          aria-selected={rivalMode === "offer"}
          onClick={() => onRivalMode("offer")}
        >
          Their offer
          <small>Type a rate the bank quoted you</small>
        </button>
      </div>
      {rivalMode === "offer" ? (
        <label className="rival-offer">
          <span>Rate they quoted</span>
          <span className="rival-offer-box">
            <input
              inputMode="decimal"
              autoComplete="off"
              placeholder="5.55"
              aria-describedby="offer-help"
              value={offerText}
              onChange={(e) => onOfferText(e.target.value.replace(/[^\d.]/g, ""))}
            />
            <em aria-hidden>%</em>
          </span>
          <small id="offer-help">Harbour side uses this instead of the cited print.</small>
        </label>
      ) : themHint ? (
        <p className="duel-hint">{themHint}</p>
      ) : null}
    </div>
  );
}
